import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, HeartPulse, TrainFront } from "lucide-react";
import { useT } from "@/i18n/LocaleContext";
import HammLogoA from "./HammLogoA";

const ICONS = { rail: TrainFront, medical: HeartPulse, sanitary: Building2 };

export default function Industries() {
  const { t, dict, locale } = useT();
  const cards = dict.industries.cards;

  return (
    <section id="branchen" className="py-32 lg:py-44 bg-[#F7F9FA] border-y border-[#E3E8EC]" data-testid="industries-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16 lg:mb-20 max-w-3xl"
        >
          <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[var(--ci-blue)] mb-6">{t("industries.eyebrow")}</p>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-[var(--ci-grey)]">
            {t("industries.title1")}<span className="text-[var(--ci-blue)]">{t("industries.title2")}</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[var(--ci-grey)] leading-relaxed">
            {t("industries.copy")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map((c, i) => {
            const Icon = ICONS[c.key] || TrainFront;
            return (
              <motion.article
                key={c.key}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, delay: i * 0.1 }}
                className="group bg-white border border-[#E3E8EC] rounded-2xl p-10 lg:p-12 hover:-translate-y-1 hover:border-[var(--ci-turquoise)]/50 hover:shadow-[0_20px_60px_rgba(0,91,234,0.08)] transition-all duration-300 flex flex-col"
                data-testid={`industry-card-${c.key}`}
              >
                <span className="w-14 h-14 rounded-2xl bg-[var(--ci-blue-light)] text-[var(--ci-blue)] flex items-center justify-center group-hover:bg-[var(--ci-blue)] group-hover:text-white transition-colors duration-300">
                  <Icon size={24} />
                </span>
                <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-[var(--ci-grey)] mt-8">{c.title}</h3>
                <p className="text-sm text-[var(--ci-grey)] leading-relaxed mt-4 flex-1">{c.desc}</p>
                <ul className="mt-8 space-y-2.5">
                  {c.tags.map((tag) => (
                    <li key={tag} className="flex items-center gap-3 font-tech text-[11px] tracking-[0.14em] uppercase text-[var(--ci-grey)]">
                      <HammLogoA data-testid="bullet-logo-a" /> {tag}
                    </li>
                  ))}
                </ul>
                <Link
                  to={locale === "en" ? "/en/inquiry-portal" : "/anfrage-portal"}
                  data-testid={`industry-cta-${c.key}`}
                  className="mt-8 self-start flex items-center gap-2 font-tech text-[11px] tracking-[0.2em] uppercase text-[var(--ci-blue)] hover:text-[var(--ci-turquoise)] transition-colors duration-200"
                >
                  {t("services.cta")} <ArrowUpRight size={14} />
                </Link>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
